/**
 * ReservationsPage — route `/reservations` (PMS portal).
 *
 * SCRUM-334 (booking reservations grid) — WIRED. This page is a thin
 * composition: it mounts the slice-internal `<ReservationsGrid>` feature
 * (the data-connected list body) and wires its two navigation callbacks to
 * the SCRUM-333 detail / create routes. It owns no state, no data-fetching,
 * and no business logic — the grid drives `useReservationsList` +
 * `useReservationTypes` + `usePropertyNameLookup` internally.
 *
 * SCRUM-333 scaffolded this surface as an honest shell (the route +
 * permission gate `booking.reservations.read`). SCRUM-334 replaces the
 * former `NotConnectedYet` placeholder body with the wired grid; the route
 * manifest, the booking barrel, and the permission gate are UNCHANGED (per
 * the epic's "replace the ReservationsPage shell in place" contract).
 *
 * Navigation wiring:
 *   - `onOpen(id)` → `/reservations/:id` (SCRUM-335 ReservationDetailPage).
 *   - `onCreate()` → `/reservations/new` (SCRUM-337 CreateReservationPage).
 * Both target routes are already declared in `app/router.tsx` (SCRUM-333).
 *
 * The permission gate is upstream (router.tsx — `booking.reservations.read`);
 * this page does not re-check. If the slug lost the read permission since
 * `/auth/me` cached, the grid surfaces its own forbidden panel off
 * `useReservationsList`'s `forbidden` status (the PropertiesPage precedent).
 *
 * Intl comes from ABOVE — the app root (`app/providers.tsx`) wraps everything
 * in `AppIntlProvider`, and the grid's money cells consume it via `useIntl`.
 * This page does NOT self-mount an intl provider (the LedgerLine precedent).
 *
 * `icon="receipt"` is the nav-model glyph for the reservations slot.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { PermissionSlug } from '@artemis/contracts';
import { hasPermission } from '../../../shared/auth/RequirePermission.js';
import { useSession } from '../../../shared/hooks/useSession.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { ReservationsGrid } from '../features/ReservationsGrid.js';

/**
 * The exact permission set the "Record channel reservation" button requires
 * (SCRUM-350 plan "Access model"). The manual channel-ingest surface calls
 * `GET /reservation-types` + `GET /reservations` (`booking.reservations.read`),
 * a property picker (`property.read`), and the ingest submit
 * (`booking.reservations.ingest`) — an ingest-only custom role would land on a
 * 403-riddled page, so the button gates on ALL THREE. Typed as
 * `PermissionSlug` (FE rule §3) so a typo fails typecheck.
 *
 * The PAGE owns this decision (not the grid): the grid is presentational and
 * receives the resolved `canRecordChannel` boolean. The three slugs cannot be
 * expressed by a single `<RequirePermission slug>`, so we compose
 * `hasPermission()` over the session user + the current-org membership (the
 * same composition `<RequirePermission>` performs).
 */
const RECORD_CHANNEL_PERMS = [
  'booking.reservations.ingest',
  'booking.reservations.read',
  'property.read',
] as const satisfies readonly PermissionSlug[];

export function ReservationsPage(): React.ReactElement {
  const navigate = useNavigate();
  const session = useSession();
  const orgState = useCurrentOrg();
  const canRecordChannel =
    session.status === 'authenticated' &&
    orgState !== null &&
    RECORD_CHANNEL_PERMS.every((slug) =>
      hasPermission(session.user, orgState.membership.organizationId, slug),
    );
  return (
    <ReservationsGrid
      onOpen={(id) => navigate(`/reservations/${id}`)}
      onCreate={() => navigate('/reservations/new')}
      canRecordChannel={canRecordChannel}
      onRecordChannel={() => navigate('/reservations/record-channel')}
    />
  );
}
